import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
// Components
import Tooltip from 'components/core/Tooltip';
import WithSkeleton from 'components/skeleton/WithSkeleton';
import _get from 'lodash/get';
import useCopyToClipboard from 'hooks/useClipboard';
import { ReactComponent as BinIcon } from 'assets/icons/bin.svg';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive.svg';
import useSmallDevice from '../../hooks/useSmallDevice';
import Status from '../Status/Status';
import { formatAddress } from '../../utils/utils';
import { AccountsContext } from '../../contexts/AccountsContext';

// Icons
import styles from './style.cssmodule.css';

function AccountRow({ account }) {
  const { formatMessage: t } = useIntl();
  const { accountsLoading, handleOpenModalArchive, handleOpenModalDeleteAccount } = useContext(AccountsContext);
  const {
    copyToClipboard, copied, justCopied, resetJustCopied,
  } = useCopyToClipboard();
  const { isSmallDevice } = useSmallDevice();

  function copyToClipboardAndStopClickPropagation(e, value) {
    e.stopPropagation();
    copyToClipboard(value);
  }

  const showSkeleton = _get(account, 'showSkeleton', false) || accountsLoading;

  // 🟠 Status
  const status = _get(account, 'status', null);
  const isArchived = status === 'ARCHIVED';

  // 👥 Username
  const id = _get(account, 'id', null);
  const firstname = _get(account, 'firstname', '');
  const lastname = _get(account, 'lastname', '');
  const fullname = firstname + (firstname ? ' ' : '') + lastname;
  const fullnameJustCopied = justCopied && copied && copied === fullname;

  // 📨 Email
  const email = isArchived ? null : _get(account, 'email', '-');
  const emailJustCopied = justCopied && copied && copied === email;

  // 🤳 Phone
  const phone = isArchived ? null : _get(account, 'phone', '-');
  const phoneJustCopied = justCopied && copied && copied === phone;

  // 📮 Address
  const address = isArchived ? null : formatAddress(_get(account, 'invoiceAddress', null));
  const addressJustCopied = justCopied && copied && copied === address;

  const totalAccesses = _get(account, 'totalAccesses', 0);
  const activeAccesses = _get(account, 'activeAccesses', 0);
  const carparksLinked = _get(account, 'carparks', []).map((carpark) => _get(carpark, 'name', '').replace(/^Parking\s/, ''));

  // 💥 Actions
  let actions = [];
  const isArchivable = _get(account, 'isArchivable', false);
  const isDeletable = _get(account, 'isDeletable', false);

  let tooltipMessage = '';

  if (isArchivable) tooltipMessage = 'account.table.actions.archive';

  if (!isArchivable) {
    if (isArchived) {
      tooltipMessage = 'account.table.actions.cannotArchiveAlreadyArchived';
    } else {
      tooltipMessage = 'account.table.actions.cannotArchive';
    }
  }
  actions = [{
    label: 'archive',
    iconButton: (
      <Tooltip content={t({ id: tooltipMessage })}>
        <button style={{ opacity: isArchivable ? 1 : 0.5 }} disabled={!isArchivable} type="button" onClick={() => handleOpenModalArchive(id)}>
          <ArchiveIcon />
        </button>
      </Tooltip>
    ),
  }];

  if (isDeletable) {
    actions = [{
      label: 'delete',
      iconButton: (
        <Tooltip content={t({ id: 'account.table.actions.delete' })}>
          <button type="button" onClick={() => handleOpenModalDeleteAccount(id)}>
            <BinIcon />
          </button>
        </Tooltip>
      ),
    }];
  }

  const Actions = actions.map(({ label, iconButton }) => (
    <div key={label}>
      {iconButton}
    </div>
  ));

  const Name = (
    <React.Fragment>
      <span>{firstname}</span>
      {firstname ? ' ' : ''}
      <b>{lastname}</b>
    </React.Fragment>
  );

  return (
    <tr key={account.id}>
      {/* 👥 Username */}
      <td className={styles.username} data-type="username">
        {WithSkeleton(
          <Tooltip
            content={
              ((firstname || lastname) && !isArchived) ? (
                <div className={styles.tooltipWithCopy}>
                  <span>{Name}</span>
                  <small>{t({ id: fullnameJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.usernameContainer}
              onClick={(e) => (firstname || lastname ? copyToClipboardAndStopClickPropagation(e, fullname) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {Name}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 140,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 📨 Email */}
      <td className={styles.email} data-type="email">
        {WithSkeleton(
          <Tooltip
            content={
              email ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{email}</div>
                  <small>{t({ id: emailJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.emailContainer}
              onClick={(e) => (email ? copyToClipboardAndStopClickPropagation(e, email) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              {email || '-'}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 150,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🤳 Phone */}
      <td className={styles.phone} data-type="phoneNumber">
        {WithSkeleton(
          <Tooltip
            content={
              phone ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{phone}</div>
                  <small>{t({ id: phoneJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.phoneContainer}
              onClick={(e) => (phone ? copyToClipboardAndStopClickPropagation(e, phone) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              <span>{phone || '-'}</span>
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 120,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 📮 Address */}
      <td className={styles.address} data-type="address">
        {WithSkeleton(
          <Tooltip
            content={
              address ? (
                <div className={styles.tooltipWithCopy}>
                  <div>{address}</div>
                  <small>{t({ id: addressJustCopied ? 'tooltip.copied' : 'tooltip.clickToCopy' })}</small>
                </div>
              ) : null
            }
          >
            <div
              className={styles.addressContainer}
              onClick={(e) => (address ? copyToClipboardAndStopClickPropagation(e, address) : null)}
              onMouseLeave={() => resetJustCopied({ delay: 200 })}
            >
              <span>{address}</span>
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 120,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🔀 Total accesses */}
      <td className={styles.totalAccesses} data-type="totalAccesses">
        {WithSkeleton(
          <span>{totalAccesses}</span>,
        )({
          loading: showSkeleton,
          width: isSmallDevice ? 30 : 60,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🔀✅ Active accesses */}
      <td className={styles.activeAccesses} data-type="activeAccesses">
        {WithSkeleton(
          <span>{activeAccesses}</span>,
        )({
          loading: showSkeleton,
          width: isSmallDevice ? 30 : 60,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🚘🚘🚘 Carpark */}
      <td className={styles.carparksLinked} data-type="carparksLinked">
        {WithSkeleton(
          <Tooltip
            content={
              carparksLinked.length > 0 ? (
                carparksLinked.map((carparkLinked) => <div key={carparkLinked}>{carparkLinked}</div>)
              ) : null
            }
          >
            <div
              className={styles.carparksLinkedContainer}
            >
              <span className={styles.firstCarparkName}>{carparksLinked.length > 0 ? carparksLinked[0] : '-'}</span>
              {carparksLinked.length > 1 ? (
                <div className="ml-1">
                  {`(+${1})`}
                </div>
              ) : null}
            </div>
          </Tooltip>,
        )({
          loading: showSkeleton,
          width: 160,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 🟠 Status */}
      <td className={styles.status} data-type="status">
        {WithSkeleton(
          <div className={styles.statusContainer}>
            <Status status={status} />
          </div>,
        )({
          loading: showSkeleton,
          width: 90,
          height: 20,
          debounceValue: 250,
        })}
      </td>
      {/* 💥 Action */}
      <td className={styles.actions} data-type="actions">
        {WithSkeleton(
          <div className={styles.actionsContainer}>
            {Actions}
          </div>,
        )({
          loading: showSkeleton,
          width: 54,
          height: 20,
          debounceValue: 250,
        })}
      </td>
    </tr>
  );
}

AccountRow.propTypes = {
  account: PropTypes.object,
};

AccountRow.defaultProps = {
  account: {},
};

export default AccountRow;



// WEBPACK FOOTER //
// ./routes/cpm/routes/accounts/components/AccountRow/AccountRow.jsx